import React, { useEffect, useState } from 'react';
import { LogOut, RefreshCw } from 'lucide-react';

const SESSION_KEY = 'primeslink_admin_password';

async function fetchLeads(password) {
  const response = await fetch('http://localhost:3001/api/admin/leads', {
    headers: { 'x-admin-password': password },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `Request failed (${response.status})`);
  }
  return response.json();
}

export default function AdminDashboard() {
  const [password, setPassword] = useState('');
  const [authedPassword, setAuthedPassword] = useState(() => sessionStorage.getItem(SESSION_KEY) || '');
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadLeads = async (pwd) => {
    setLoading(true);
    setError('');
    try {
      const { leads: data } = await fetchLeads(pwd);
      setLeads(data || []);
      sessionStorage.setItem(SESSION_KEY, pwd);
      setAuthedPassword(pwd);
    } catch (err) {
      setError(err.message);
      sessionStorage.removeItem(SESSION_KEY);
      setAuthedPassword('');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authedPassword) loadLeads(authedPassword);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogin = (e) => {
    e.preventDefault();
    loadLeads(password);
  };

  const handleLogout = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setAuthedPassword('');
    setLeads([]);
    setPassword('');
  };

  if (!authedPassword) {
    return (
      <div className="gradient-bg min-h-screen flex items-center justify-center px-4">
        <form
          onSubmit={handleLogin}
          className="w-full max-w-sm p-8 rounded-2xl glass-dark border border-white/10"
        >
          <h1 className="text-xl font-bold text-white mb-1">Primeslink Admin</h1>
          <p className="text-sm text-gray-400 mb-6">Enter the dashboard password to continue.</p>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            autoFocus
            className="w-full px-4 py-2 mb-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary-500"
          />
          {error && <p className="text-sm text-red-400 mb-3">{error}</p>}
          <button
            type="submit"
            disabled={loading || !password}
            className="w-full py-2 bg-gradient-to-r from-primary-500 to-accent-500 text-white rounded-lg font-semibold disabled:opacity-50"
          >
            {loading ? 'Checking...' : 'Login'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="gradient-bg min-h-screen px-4 sm:px-6 lg:px-8 py-10">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">Leads Dashboard</h1>
            <p className="text-sm text-gray-400">{leads.length} lead{leads.length === 1 ? '' : 's'} captured</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => loadLeads(authedPassword)}
              className="p-2 rounded-lg glass hover:bg-white/10 text-gray-300"
              title="Refresh"
            >
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            </button>
            <button
              onClick={handleLogout}
              className="p-2 rounded-lg glass hover:bg-white/10 text-gray-300"
              title="Logout"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>

        {error && (
          <p className="text-sm text-red-400 mb-4">{error}</p>
        )}

        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-sm text-left">
            <thead className="bg-dark-900/80 text-gray-400 uppercase text-xs">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3">Company</th>
                <th className="px-4 py-3">Business Type</th>
                <th className="px-4 py-3">Needs</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead, idx) => (
                <tr
                  key={lead.id ?? idx}
                  className="border-t border-white/10 text-gray-300 hover:bg-white/5"
                >
                  <td className="px-4 py-3 text-white font-medium">{lead.name || '—'}</td>
                  <td className="px-4 py-3">{lead.email || '—'}</td>
                  <td className="px-4 py-3">{lead.phone || '—'}</td>
                  <td className="px-4 py-3">{lead.company_name || '—'}</td>
                  <td className="px-4 py-3">{lead.business_type || '—'}</td>
                  <td className="px-4 py-3 max-w-xs truncate" title={lead.specific_needs}>
                    {lead.specific_needs || '—'}
                  </td>
                </tr>
              ))}
              {!loading && leads.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    No leads yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
